import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Modal } from './Modal';

export interface ConfirmOptions {
  title: string;
  /** The consequence, in plain words. Skip it when the title says everything. */
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Styles the confirm button as destructive and colours the icon. */
  danger?: boolean;
}

type Ask = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Ask | null>(null);

/**
 * Replaces window.confirm.
 *
 * The native dialog can't be styled, announces the Railway hostname above the
 * question, and looks like a security warning rather than part of the app —
 * which is exactly the wrong tone for "shall I archive this child?".
 *
 * The hook returns a promise so call sites read the same as before:
 *   if (await confirm({ ... })) doTheThing();
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  const ask = useCallback<Ask>((options) => {
    setRequest(options);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setRequest(null);
  }, []);

  // Escape cancels, as it does in every other dialog in the app.
  useEffect(() => {
    if (!request) return;
    confirmRef.current?.focus();

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') settle(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [request, settle]);

  return (
    <ConfirmContext.Provider value={ask}>
      {children}

      {request && (
        <Modal
          // Clicking away from a question is answering no, which is the
          // safe half of every question this asks.
          onClose={() => settle(false)}
          label={request.title}
          backdrop="bg-espresso/40 print:hidden"
          zIndex={80}
          className="w-full max-w-sm rounded-card border border-sand bg-white p-5 shadow-lifted"
        >
          <div role="alertdialog" aria-label={request.title}>
            <div className="flex gap-3">
              <span
                aria-hidden
                className={[
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-lg',
                  request.danger ? 'bg-rose/20 text-rust' : 'bg-linen text-walnut',
                ].join(' ')}
              >
                {request.danger ? '!' : '?'}
              </span>
              <div className="min-w-0">
                <h2 className="text-base leading-snug text-espresso">{request.title}</h2>
                {request.body && (
                  <p className="mt-1 text-sm leading-relaxed text-walnut">{request.body}</p>
                )}
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => settle(false)} className="btn-secondary">
                {request.cancelLabel ?? 'Cancel'}
              </button>
              <button
                ref={confirmRef}
                onClick={() => settle(true)}
                className={request.danger ? 'btn-danger' : 'btn-primary'}
              >
                {request.confirmLabel ?? 'Continue'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): Ask {
  const ask = useContext(ConfirmContext);
  if (!ask) throw new Error('useConfirm must be used inside ConfirmProvider');
  return ask;
}
